/**
 * Santé de la plateforme (SUPER_ADMIN).
 * Un chiffre par organe dans l'en-tête, les alertes derrière l'icône d'alerte,
 * et la dernière sauvegarde. Rafraîchi toutes les 60 secondes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, RefreshCw } from 'lucide-react';
import { backendOpsAPI } from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatNombre } from '../lib/formats';

const DELAI_RAFRAICHISSEMENT_MS = 60 * 1000;
const AGE_MAX_SAUVEGARDE_MS = 48 * 60 * 60 * 1000;

const etat = (ok) => (ok ? 'positif' : 'critique');
const pourcent = (valeur) => (valeur != null ? `${valeur} %` : '—');
const serviceValeur = (service) =>
  service?.latency_ms ? `${service.latency_ms} ms` : service?.status === 'up' ? 'En service' : 'Hors service';

/** La sauvegarde la plus récente, quel que soit l'ordre de la liste. */
export const derniereSauvegarde = (sauvegardes) => {
  const datees = (Array.isArray(sauvegardes) ? sauvegardes : []).filter(
    (s) => s?.created_at && !Number.isNaN(new Date(s.created_at).getTime()),
  );
  if (datees.length === 0) return null;
  return datees.reduce((a, b) => (new Date(b.created_at) > new Date(a.created_at) ? b : a));
};

export default function PlateformeSantePage() {
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState(null);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(async (silencieux = false) => {
    if (!silencieux) setLoading(true);
    const [h, b] = await Promise.allSettled([backendOpsAPI.getHealth(), backendOpsAPI.listBackups()]);
    if (h.status === 'fulfilled') setHealth(h.value);
    if (b.status === 'fulfilled') setBackups(b.value || []);
    if (!silencieux && h.status === 'rejected' && b.status === 'rejected') {
      toast.error(messageErreur(h.reason, 'Impossible de charger la santé de la plateforme'));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    charger();
    const minuteur = setInterval(() => charger(true), DELAI_RAFRAICHISSEMENT_MS);
    return () => clearInterval(minuteur);
  }, [charger]);

  const svc = health?.services || {};
  const sys = health?.system || {};
  const derniere = derniereSauvegarde(backups);

  const baseOk = svc.mongodb?.status === 'up';
  const cacheOk = svc.redis?.status === 'up';
  const detailBase = svc.mongodb?.data_size_mb ? `${svc.mongodb.data_size_mb} Mo` : undefined;
  const detailRapide = svc.redis?.used_memory_mb ? `${svc.redis.used_memory_mb} Mo` : undefined;
  const detailMemoire = `${sys.memory_used_mb?.toFixed?.(0) || '—'} sur ${sys.memory_total_mb?.toFixed?.(0) || '—'} Mo`;
  const detailDisque = `${sys.disk_used_gb?.toFixed?.(1) || '—'} sur ${sys.disk_total_gb?.toFixed?.(1) || '—'} Go`;

  const stats = [
    { label: 'Base de données', valeur: serviceValeur(svc.mongodb), ton: etat(baseOk), aide: detailBase },
    { label: 'Mémoire rapide', valeur: serviceValeur(svc.redis), ton: etat(cacheOk), aide: detailRapide },
    { label: 'Mémoire', valeur: pourcent(sys.memory_percent), ton: etat((sys.memory_percent ?? 0) < 80), aide: detailMemoire },
    { label: 'Processeur', valeur: pourcent(sys.cpu_percent), ton: etat((sys.cpu_percent ?? 0) < 80) },
    { label: 'Disque', valeur: pourcent(sys.disk_percent), ton: etat((sys.disk_percent ?? 0) < 85), aide: detailDisque },
  ];

  const sauvegardeChargee = backups !== null;
  const sauvegardeTropAncienne =
    derniere && Date.now() - new Date(derniere.created_at).getTime() > AGE_MAX_SAUVEGARDE_MS;

  const alertes = [
    health && (sys.memory_percent ?? 0) >= 80 && {
      niveau: 'attention',
      court: 'Mémoire presque pleine',
      texte: `La mémoire du système est utilisée à ${sys.memory_percent} % (${detailMemoire}).`,
    },
    health && (sys.disk_percent ?? 0) >= 85 && {
      niveau: 'critique',
      court: 'Disque presque plein',
      texte: `Le disque est utilisé à ${sys.disk_percent} % (${detailDisque}).`,
    },
    health && !baseOk && {
      niveau: 'critique',
      court: 'Base de données hors service',
      texte: 'La base de données ne répond pas.',
    },
    health && !cacheOk && {
      niveau: 'critique',
      court: 'Mémoire rapide hors service',
      texte: 'La mémoire rapide ne répond pas.',
    },
    sauvegardeChargee && !derniere && {
      niveau: 'critique',
      court: 'Aucune sauvegarde',
      texte: 'Aucune sauvegarde n’existe pour le moment.',
    },
    sauvegardeTropAncienne && {
      niveau: 'attention',
      court: 'Sauvegarde ancienne',
      texte: `La dernière sauvegarde date du ${formatDateHeure(derniere.created_at)}, soit plus de 48 heures.`,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Santé de la plateforme"
        description="État des organes du système et dernière sauvegarde, mis à jour chaque minute."
        stats={stats}
        alertesSousTitre
        alertes={alertes}
        actions={
          <button onClick={() => charger()} className="btn-secondary px-3" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        }
      />

      <div className="rounded-2xl border border-border bg-card shadow-sm" data-testid="derniere-sauvegarde">
        <div className="flex items-center justify-between border-b border-border/60 p-4">
          <div className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-flame-ink" />
            <h2 className="uppercase text-muted-foreground">Dernière sauvegarde</h2>
          </div>
          <Link to="/backend-ops" className="btn-secondary px-3">
            Gérer les sauvegardes
          </Link>
        </div>
        <div className="p-4">
          {!sauvegardeChargee ? (
            <div className="text-sm text-muted-foreground/70">—</div>
          ) : derniere ? (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <div className="text-sm font-semibold text-foreground">{formatDateHeure(derniere.created_at)}</div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {derniere.size_mb != null ? `${formatNombre(derniere.size_mb, 1)} Mo` : '—'}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground/70">Aucune sauvegarde pour le moment.</div>
          )}
        </div>
      </div>
    </div>
  );
}
